import { Fragment, useCallback, useEffect, useState } from 'react';
import { CURRENCIES } from '@wyocspay/shared/regions';
import {
  Badge,
  CheckIcon,
  decimals,
  Empty,
  Field,
  LinkIcon,
  Note,
  Panel,
  RefreshIcon,
  Spinner,
  TableWrap,
  XIcon,
} from '@wyocspay/ui';
import { dateTime, money, relative } from './format.js';
import { ApiError, api, type PaymentLinkRow } from './api.js';

/**
 * Create a payment and hand the customer a link.
 *
 * Amounts are entered in major units because that is how people think about
 * money, and converted to minor units here - the API only ever sees integers.
 */
export function NewPaymentView({
  onOpen,
  defaultCurrency,
}: {
  onOpen: (id: string) => void;
  defaultCurrency: string;
}) {
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState(() => {
    const code = defaultCurrency.toLowerCase();
    return CURRENCIES.some((candidate) => candidate.code === code) ? code : 'usd';
  });
  const [description, setDescription] = useState('');
  const [reference, setReference] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<{ id: string; url: string; amount: number; currency: string } | null>(null);
  const [copied, setCopied] = useState(false);

  const places = decimals(currency);
  const parsed = Number.parseFloat(amount);
  const minor = Number.isFinite(parsed) ? Math.round(parsed * 10 ** places) : NaN;
  const valid = Number.isFinite(minor) && minor > 0;

  async function submit(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const payment = await api.createPayment({
        amount: minor,
        currency,
        description: description.trim() || undefined,
        merchant_reference: reference.trim() || undefined,
      });
      setCreated({
        id: payment.id,
        url: payment.payment_url,
        amount: payment.amount,
        currency: payment.currency,
      });
      setAmount('');
      setDescription('');
      setReference('');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  if (created) {
    return (
      <div style={{ display: 'grid', gap: 14, maxWidth: 640 }}>
        <Note tone="ok" title={`Payment for ${money(created.amount, created.currency)} created`}>
          Send this link to your customer. It is valid for 24 hours and can be renewed if they run
          out of time.
        </Note>

        <Panel title="Payment link">
          <pre
            className="wc-num"
            style={{ margin: 0, padding: 12, background: 'var(--wc-surface-alt)', border: '1px solid var(--wc-border)', fontSize: 12.5, wordBreak: 'break-all', whiteSpace: 'pre-wrap' }}
          >
            {created.url}
          </pre>

          <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
            <button
              type="button"
              className="wc-btn wc-btn-sm"
              onClick={async () => {
                await navigator.clipboard.writeText(created.url);
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
              }}
            >
              {copied ? <><CheckIcon size={13} /> Copied</> : 'Copy link'}
            </button>
            <a
              className="wc-btn wc-btn-sm wc-btn-secondary"
              href={created.url}
              target="_blank"
              rel="noreferrer"
              style={{ textDecoration: 'none' }}
            >
              Open as the customer sees it
            </a>
            <button type="button" className="wc-btn wc-btn-sm wc-btn-secondary" onClick={() => onOpen(created.id)}>
              View payment
            </button>
          </div>
        </Panel>

        <button type="button" className="wc-btn wc-btn-secondary" onClick={() => setCreated(null)} style={{ justifySelf: 'start' }}>
          Create another
        </button>
      </div>
    );
  }

  return (
    <div style={{ display: 'grid', gap: 14, maxWidth: 560 }}>
      <Panel title="New payment">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label="Amount" hint={places === 0 ? 'Whole units' : `Up to ${places} decimal places`}>
            <input
              className="wc-input wc-num"
              inputMode="decimal"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder={places === 0 ? '1000' : '49.90'}
              autoFocus
            />
          </Field>
          <Field label="Currency">
            <select className="wc-input" value={currency} onChange={(e) => setCurrency(e.target.value)}>
              {CURRENCIES.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.symbol} {c.name} ({c.code.toUpperCase()})
                </option>
              ))}
            </select>
          </Field>
        </div>

        <Field label="Description" hint="Shown to the customer on the payment page.">
          <input
            className="wc-input"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Monthly subscription - July 2026"
          />
        </Field>

        <Field label="Your reference" hint="Your own invoice or order number. Optional.">
          <input
            className="wc-input wc-num"
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            placeholder="INV-2026-0415"
          />
        </Field>

        {valid ? (
          <div style={{ marginBottom: 14 }}>
            <Note>
              The customer will be asked for <strong>{money(minor, currency)}</strong>.
            </Note>
          </div>
        ) : null}

        {error ? <div style={{ marginBottom: 14 }}><Note tone="danger">{error}</Note></div> : null}

        <button type="button" className="wc-btn" onClick={submit} disabled={busy || !valid}>
          <LinkIcon size={14} /> {busy ? 'Creating…' : 'Create payment and link'}
        </button>
      </Panel>
    </div>
  );
}

/**
 * Link management for one payment.
 *
 * Renew keeps the URL the customer already has; reissue mints a new one. The
 * distinction matters operationally - chasing a customer with a replacement
 * URL is worse than quietly extending the one in their inbox.
 */
export function PaymentLinks({ paymentId, currency }: { paymentId: string; currency: string }) {
  const [links, setLinks] = useState<PaymentLinkRow[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ tone: 'ok' | 'warn' | 'danger'; text: string; url?: string } | null>(null);
  /**
   * Which link the merchant has asked to cancel, and has not yet confirmed.
   *
   * Held here rather than in each row so only one confirmation can be open at a
   * time: two open at once and the wrong one gets confirmed. Cancelling a link
   * cannot be undone and the customer holding that URL finds it dead, so the
   * confirmation states both of those before the destructive button appears.
   */
  const [confirming, setConfirming] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [emailTo, setEmailTo] = useState('');

  const load = useCallback(async () => {
    try {
      setLinks((await api.paymentLinks(paymentId)).data);
    } catch {
      setLinks([]);
    }
  }, [paymentId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function act(mode: 'renew' | 'reissue'): Promise<void> {
    setBusy(true);
    setMsg(null);
    setConfirming(null);
    try {
      const r = await api.paymentLink(paymentId, mode);
      setMsg(
        mode === 'renew'
          ? { tone: 'ok', text: `The link the customer already has now works until ${dateTime(r.expires_at)}.` }
          : { tone: 'warn', text: `New link, valid until ${dateTime(r.expires_at)}. The old one keeps working until it expires - revoke it if it leaked.`, url: r.url ?? undefined },
      );
      await load();
    } catch (err) {
      setMsg({ tone: 'danger', text: err instanceof ApiError ? err.message : String(err) });
    } finally {
      setBusy(false);
    }
  }

  /**
   * Issue a link and email it, in one call.
   *
   * The URL is not passed to the server - it is minted there, from this payment
   * - so this cannot be talked into mailing anything else. It comes back so the
   * merchant can copy it as well as send it, since this is the only moment it
   * is knowable.
   */
  async function email(): Promise<void> {
    setBusy(true);
    setMsg(null);
    try {
      const r = await api.emailPaymentLink(paymentId, { to: emailTo });
      setMsg({
        tone: 'ok',
        text: `Sent to ${r.to}. The link works until ${dateTime(r.expires_at)}.`,
        url: r.url,
      });
      setEmailTo('');
      await load();
    } catch (err) {
      setMsg({ tone: 'danger', text: err instanceof ApiError ? err.message : String(err) });
    } finally {
      setBusy(false);
    }
  }

  async function revoke(linkId: string): Promise<void> {
    setBusy(true);
    setMsg(null);
    try {
      await api.revokePaymentLink(paymentId, linkId);
      setConfirming(null);
      setMsg({
        tone: 'warn',
        text:
          'That link is cancelled. Anyone opening its URL now sees "this payment link has been ' +
          'cancelled". Issue a new link if the customer still needs to pay.',
      });
      await load();
    } catch (err) {
      setMsg({ tone: 'danger', text: err instanceof ApiError ? err.message : String(err) });
    } finally {
      setBusy(false);
    }
  }

  const live = links?.filter((l) => !l.revoked && !l.expired).length ?? 0;

  return (
    <Panel
      title="Payment links"
      padded={false}
      actions={
        <div style={{ display: 'flex', gap: 6 }}>
          <button type="button" className="wc-btn wc-btn-sm" disabled={busy} onClick={() => act('renew')}>
            <RefreshIcon size={12} /> Renew
          </button>
          <button type="button" className="wc-btn wc-btn-sm wc-btn-secondary" disabled={busy} onClick={() => act('reissue')}>
            New link
          </button>
        </div>
      }
    >
      {msg ? (
        <div style={{ padding: 16, paddingBottom: 0 }}>
          <Note tone={msg.tone}>
            {msg.text}
            {msg.url ? (
              <>
                <pre className="wc-num" style={{ margin: '8px 0 0', fontSize: 12, wordBreak: 'break-all', whiteSpace: 'pre-wrap' }}>
                  {msg.url}
                </pre>
                {/*
                  The only moment this URL exists anywhere but the customer's
                  hands. Shown once, so it is copyable once - printing it without
                  a button to take it is how somebody loses it.
                */}
                <div style={{ marginTop: 8 }}>
                  <button
                    type="button"
                    className="wc-btn wc-btn-sm"
                    onClick={async () => {
                      await navigator.clipboard.writeText(msg.url!);
                      setCopied(true);
                      setTimeout(() => setCopied(false), 2000);
                    }}
                  >
                    {copied ? <><CheckIcon size={13} /> Copied</> : 'Copy link'}
                  </button>
                </div>
              </>
            ) : null}
          </Note>
        </div>
      ) : null}

      {links === null ? (
        <div style={{ padding: 16 }}><Spinner label="Loading links…" /></div>
      ) : links.length === 0 ? (
        <Empty>No links issued for this payment.</Empty>
      ) : (
        <>
          {/*
            The two questions this panel gets asked, answered where it is asked.
          *
            Both used to be answerable only by reading twelve-point grey text at
            the bottom of the panel, which is another way of saying they were not
            answered. "Where do I copy the link again" has an unwelcome answer -
            nowhere - and burying it does not make it less true, it just means
            somebody hunts for a button that cannot exist before finding the one
            that solves their actual problem.
          */}
          <div style={{ padding: '12px 16px 0' }}>
            <Note title="Lost the link, or need to kill one?">
              <strong>Lost it:</strong> press <strong>New link</strong>. The URL is shown once when
              it is created and never stored - only its hash is kept, exactly as with an API key -
              so no screen anywhere can show you the old one again. A new link is issued instantly
              and comes with a copy button. The old one keeps working until it expires, so cancel it
              below if it went somewhere it should not have.
              <div style={{ marginTop: 6 }}>
                <strong>Kill one:</strong> <strong>Cancel link</strong> on its row. The URL dies
                immediately and for good; the payment itself stays open.
              </div>
              <div style={{ marginTop: 6, fontSize: 12.5, color: 'var(--wc-muted)' }}>
                {live} live · {links.length} total. Renewing extends the newest live link rather
                than adding a row. Cancelled links stay listed so you can see when a URL stopped
                working. Currency: {currency.toUpperCase()}.
              </div>
            </Note>

            {/*
              Sending, beside the explanation of why the URL cannot be shown
              again. This is the answer to "I lost it" that does not involve
              copying anything: the customer gets it directly, from the
              merchant's own address.
            */}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end', marginTop: 12 }}>
              <div style={{ flex: '1 1 220px', minWidth: 0 }}>
                <label style={{ display: 'block', fontSize: 12.5, color: 'var(--wc-faint)', marginBottom: 4 }}>
                  Email a new link to the customer
                </label>
                <input
                  className="wc-input"
                  type="email"
                  value={emailTo}
                  placeholder="customer@example.com"
                  onChange={(e) => setEmailTo(e.target.value)}
                  style={{ width: '100%' }}
                />
              </div>
              <button
                type="button"
                className="wc-btn wc-btn-sm"
                onClick={() => void email()}
                disabled={busy || !emailTo}
              >
                {busy ? 'Sending…' : 'Issue and send'}
              </button>
            </div>
          </div>
          <TableWrap>
            <table className="wc-table">
              <thead>
                <tr>
                  <th>Issued</th>
                  <th>Expires</th>
                  <th>State</th>
                  <th style={{ textAlign: 'right' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {links.map((l) => {
                  /**
                   * Only a link that still works can be cancelled. Offering the
                   * button on an expired or already-cancelled one would promise
                   * an effect it cannot have - the URL is dead either way.
                   */
                  const cancellable = !l.revoked && !l.expired;
                  const open = confirming === l.id;

                  return (
                    <Fragment key={l.id}>
                      <tr>
                        <td style={{ fontSize: 13, color: 'var(--wc-muted)' }}>{relative(l.created_at)}</td>
                        <td style={{ fontSize: 13 }}>{dateTime(l.expires_at)}</td>
                        <td>
                          <Badge tone={l.revoked ? 'fail' : l.expired ? 'neutral' : 'ok'}>
                            {l.revoked ? 'CANCELLED' : l.expired ? 'EXPIRED' : 'LIVE'}
                          </Badge>
                          {l.revoked && l.revoked_at ? (
                            <div style={{ fontSize: 12, color: 'var(--wc-muted)', marginTop: 3 }}>
                              {relative(l.revoked_at)}
                            </div>
                          ) : null}
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          {cancellable ? (
                            <button
                              type="button"
                              className="wc-btn wc-btn-sm wc-btn-secondary"
                              onClick={() => setConfirming(open ? null : l.id)}
                              disabled={busy}
                              aria-expanded={open}
                              aria-label={`Cancel the link issued ${relative(l.created_at)}`}
                            >
                              {open ? 'Keep it' : 'Cancel link'}
                            </button>
                          ) : (
                            <span style={{ fontSize: 12.5, color: 'var(--wc-faint)' }}>
                              {l.revoked ? 'Cancelled' : 'Already dead'}
                            </span>
                          )}
                        </td>
                      </tr>

                      {open ? (
                        <tr>
                          <td colSpan={4} style={{ background: 'var(--wc-surface-alt)' }}>
                            <div style={{ display: 'grid', gap: 10, padding: '2px 0 4px' }}>
                              <Note tone="danger" title="Cancel this link?">
                                The URL stops working immediately and cannot be brought back. A
                                customer who already has it will be told the payment link has been
                                cancelled. The payment itself stays open - issue a new link if they
                                still need to pay.
                              </Note>
                              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                <button
                                  type="button"
                                  className="wc-btn wc-btn-sm wc-btn-danger"
                                  onClick={() => void revoke(l.id)}
                                  disabled={busy}
                                >
                                  <XIcon size={12} /> {busy ? 'Cancelling…' : 'Yes, cancel it'}
                                </button>
                                <button
                                  type="button"
                                  className="wc-btn wc-btn-sm wc-btn-secondary"
                                  onClick={() => setConfirming(null)}
                                  disabled={busy}
                                >
                                  Keep it working
                                </button>
                              </div>
                            </div>
                          </td>
                        </tr>
                      ) : null}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </TableWrap>
        </>
      )}
    </Panel>
  );
}
